import React from 'react';
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, ReferenceLine,
} from 'recharts';

// Artifact emitted by the get_performance tool (kind: "performance").
export interface PerformanceData {
  name: string;
  ticker: string;
  type?: string;
  series: { date: string; price: number }[];
  return_pct?: number | null;
  from?: string;
  to?: string;
}

const NAVY = '#000537';

const pct = (v: number) => `${v >= 0 ? '+' : ''}${(v * 100).toFixed(1)}%`;
const shortDate = (d: string) => {
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? d : dt.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
};

const PerformanceChart: React.FC<{ data: PerformanceData }> = ({ data }) => {
  const series = data.series || [];
  const base = series.length ? series[0].price : 0;
  // Normalized return from the start — currency-agnostic ("performance").
  const points = series.map((p) => ({ date: p.date, ret: base ? p.price / base - 1 : 0 }));
  const ret = data.return_pct ?? (points.length ? points[points.length - 1].ret : 0);
  const up = ret >= 0;
  const color = up ? '#15803d' : '#b91c1c';

  return (
    <div className="chart-card">
      <div className="d-flex justify-content-between align-items-baseline" style={{ marginBottom: 10 }}>
        <span className="fw-semibold" style={{ fontSize: '0.85rem', color: NAVY }}>
          {data.name} <span style={{ color: '#a1a1aa', fontWeight: 400 }}>{data.ticker}</span>
        </span>
        <span style={{ fontSize: '0.85rem', fontWeight: 600, color }}>{pct(ret)} <span style={{ color: '#a1a1aa', fontWeight: 400, fontSize: '0.72rem' }}>1Y</span></span>
      </div>

      {points.length > 1 ? (
        <ResponsiveContainer width="100%" height={200}>
          <AreaChart data={points} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
            <defs>
              <linearGradient id="perfFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={color} stopOpacity={0.18} />
                <stop offset="100%" stopColor={color} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid vertical={false} stroke="#f1f1f3" />
            <XAxis dataKey="date" tickFormatter={shortDate} tick={{ fontSize: 11, fill: '#71717a' }} minTickGap={40} />
            <YAxis tickFormatter={(v: number) => pct(v)} tick={{ fontSize: 11, fill: '#71717a' }} width={48} />
            <ReferenceLine y={0} stroke="#d4d4d8" />
            <Tooltip
              formatter={(v: number) => pct(v)}
              labelFormatter={shortDate}
              contentStyle={{ fontSize: 12, borderRadius: 8 }}
            />
            <Area type="monotone" dataKey="ret" stroke={color} strokeWidth={2} fill="url(#perfFill)" />
          </AreaChart>
        </ResponsiveContainer>
      ) : (
        <div style={{ fontSize: '0.8rem', color: '#a1a1aa' }}>No price history available.</div>
      )}
    </div>
  );
};

export default PerformanceChart;
